/* eslint-disable no-undef */
import React, { memo, useCallback, useEffect, useState } from 'react';
import { GoogleMap, useJsApiLoader, Marker, Autocomplete } from '@react-google-maps/api';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';

import RightModalMC from '../../components/RightModalMC';
import ButtonMC from '../../components/ButtonMC';
import { geocoderLocationInfo } from '../../../util/utils';

const debugLog = (hypothesisId, location, message, data = {}) => {
  // #region agent log
  fetch('http://127.0.0.1:7665/ingest/596c55f7-50c2-410c-b527-ec7ee0b81d6e', { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': '32cca4' }, body: JSON.stringify({ sessionId: '32cca4', runId: 'pre-fix', hypothesisId, location, message, data, timestamp: Date.now() }) }).catch(() => {});
  // #endregion
};

const containerStyle = {
  position: 'relative',
  width: '100%',
  height: 'calc(100vh - 280px)'
};

const googleMapsLibraries = ['places'];

const MapComponentMC = ({
  toggleModal,
  addressData,
  renderModal = true
}) => {
  const DEFAULT_ZOOM = 16;

  const { t } = useTranslation();

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: ENV['GOOGLE_API_KEY'],
    libraries: googleMapsLibraries
  });

  const defaultPosition = { lat: -0.190599, lng: -78.485619 };

  const [map, setMap] = useState(null);
  const [newAddress, setNewAddress] = useState({});
  const [markerPosition, setMarkerPosition] = useState(defaultPosition);
  const [center, setCenter] = useState(defaultPosition);
  const [autoComplete, setAutoComplete] = useState(null);
  const [isResolvingAddress, setIsResolvingAddress] = useState(false);

  const onLoad = useCallback(function callback(map) {
    setMap(map);
  }, []);

  const onUnmount = useCallback(function callback(map) {
    setMap(null);
  }, []);

  useEffect(() => {
    if (!map) return;

    if (addressData?.latitude && addressData?.longitude) {
      const newPosition = { lat: Number(addressData.latitude), lng: Number(addressData.longitude) };
      setMarkerPosition(newPosition);
      setCenter(newPosition);
      map.setCenter(newPosition);
      setNewAddress({ ...addressData });
    } else {
      loadLocation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  const getHumanAddress = useCallback((newPosition) => {
    const geocoder = new window.google.maps.Geocoder();
    setIsResolvingAddress(true);
    debugLog('H6', 'MapComponentMC.js:getHumanAddress:request', 'Reverse geocode request', {
      lat: Number(newPosition?.lat?.toFixed ? newPosition.lat.toFixed(6) : newPosition?.lat),
      lng: Number(newPosition?.lng?.toFixed ? newPosition.lng.toFixed(6) : newPosition?.lng)
    });

    geocoder.geocode({ location: newPosition }, (results, status) => {
      debugLog('H6', 'MapComponentMC.js:getHumanAddress:response', 'Reverse geocode response', {
        status,
        resultsCount: results?.length || 0,
        firstFormattedAddress: results?.[0]?.formatted_address || null
      });
      if (status === 'OK' && results[0]) {
        const locationInfo = geocoderLocationInfo(results);
        setNewAddress((prevAddress) => ({ ...prevAddress, ...locationInfo }));
      }

      setIsResolvingAddress(false);
    });
  }, []);

  const resolveAddressForPosition = useCallback((position) => new Promise((resolve) => {
    const geocoder = new window.google.maps.Geocoder();
    debugLog('H7', 'MapComponentMC.js:resolveAddressForPosition:request', 'Resolve-on-save geocode request', {
      lat: Number(position?.lat?.toFixed ? position.lat.toFixed(6) : position?.lat),
      lng: Number(position?.lng?.toFixed ? position.lng.toFixed(6) : position?.lng),
      positionType: typeof position?.lat
    });
    geocoder.geocode({ location: position }, (results, status) => {
      debugLog('H7', 'MapComponentMC.js:resolveAddressForPosition:response', 'Resolve-on-save geocode response', {
        status,
        resultsCount: results?.length || 0,
        firstFormattedAddress: results?.[0]?.formatted_address || null
      });
      if (status === 'OK' && results[0]) {
        resolve(geocoderLocationInfo(results));
        return;
      }

      resolve({});
    });
  }), []);

  const loadLocation = useCallback(() => {
    if (!map) return;
    
    navigator.geolocation.getCurrentPosition((position) => {
      if (position && position.coords && position.coords.latitude && position.coords.longitude) {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        const newPosition = { lat, lng };

        getHumanAddress(newPosition);
        setMarkerPosition(newPosition);
        setCenter(newPosition);
        map.setCenter(newPosition);
      }
    });
  }, [map, getHumanAddress]);

  const handelClickOnMap = (e) => {
    const newPosition = { lat: e.latLng.lat(), lng: e.latLng.lng() };
    getHumanAddress(newPosition);
    setMarkerPosition(newPosition);
    setCenter(newPosition);
  };

  const onLoadAutocomplete = (service) => {
    setAutoComplete(service);
  };

  const onPlaceChanged = () => {
    if (isEmpty(autoComplete)) return;

    const place = autoComplete.getPlace();
    if (!place) return;

    if (!place.geometry || !place.geometry.location) {
      alert(t('customer.address.error'));
      return;
    }

    if (place.geometry.viewport || place.geometry.location) {
      const newPosition = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng()
      };

      setNewAddress((prevAddress) => ({
        ...prevAddress,
        address: place.formatted_address || prevAddress.address || ''
      }));
      setMarkerPosition(newPosition);
      setCenter(newPosition);
      map?.setCenter(newPosition);
      getHumanAddress(newPosition);
    }
  };

  const handleToggleModal = async () => {
    debugLog('H1', 'MapComponentMC.js:handleToggleModal:entry', 'Map save pressed', {
      isResolvingAddress,
      hasAddressData: Boolean(addressData),
      hasNewAddress: Boolean(newAddress),
      hasNewAddressText: Boolean(newAddress?.address)
    });
    if (isResolvingAddress) return;

    setIsResolvingAddress(true);

    const resolvedAddress = newAddress.address
      ? newAddress
      : await resolveAddressForPosition(markerPosition);
    debugLog('H2', 'MapComponentMC.js:handleToggleModal:resolved', 'Resolved address for map position', {
      resolvedKeysCount: resolvedAddress ? Object.keys(resolvedAddress).length : 0,
      resolvedHasAddress: Boolean(resolvedAddress?.address),
      resolvedHasCity: Boolean(resolvedAddress?.city),
      resolvedHasState: Boolean(resolvedAddress?.state),
      resolvedHasZip: Boolean(resolvedAddress?.zip_code)
    });

    const addressDataResult = {
      ...addressData,
      ...resolvedAddress,
      ...newAddress,
      latitude: markerPosition.lat,
      longitude: markerPosition.lng
    };

    setIsResolvingAddress(false);
    debugLog('H2', 'MapComponentMC.js:handleToggleModal:exit', 'Returning data to parent modal', {
      resultKeysCount: Object.keys(addressDataResult).length,
      resultHasAddress: Boolean(addressDataResult?.address),
      resultHasCity: Boolean(addressDataResult?.city),
      resultHasState: Boolean(addressDataResult?.state),
      resultHasZip: Boolean(addressDataResult?.zip_code),
      resultHasLat: Boolean(addressDataResult?.latitude),
      resultHasLng: Boolean(addressDataResult?.longitude)
    });
    toggleModal(addressDataResult);
  };

  const handleClose = () => {
    toggleModal(null);
  };

  const mapContent = (
    <div className="tw-flex tw-flex-col tw-h-full tw-overflow-hidden">
      {/* Body */}
      <div className="tw-flex-1 tw-overflow-y-auto tw-px-6 tw-py-4">
          {/* Address Search Input */}
          <div className="tw-w-full tw-mb-6">
            <div className="tw-wrapper">
              <div className="tw-relative">
                <Autocomplete
                  onLoad={onLoadAutocomplete}
                  onPlaceChanged={onPlaceChanged}
                  className="tw-w-full"
                >
                  <input
                    type="text"
                    id="searchTextField"
                    className="tw-block tw-rounded-xl tw-px-4 tw-pt-5 tw-pb-0 tw-w-full tw-text-default tw-bg-gray-50 tw-border-gray-200 tw-appearance-none focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 peer tw-border"
                    placeholder=""
                    value={newAddress.address || ''}
                    onChange={(e) => setNewAddress({ ...newAddress, address: e.target.value })}
                  />
                </Autocomplete>
                <label
                  htmlFor="searchTextField"
                  className="tw-absolute tw-text-xs tw-text-blue-500 tw-top-[5px] tw-z-10 tw-start-4 tw-pointer-events-none"
                >
                  {t('customer.address.address')}
                </label>
              </div>
            </div>
          </div>

        {/* Google Map */}
        {isLoaded && (
          <div className="tw-mt-5">
            <GoogleMap
              id="google-map-script"
              onLoad={onLoad}
              center={center}
              zoom={DEFAULT_ZOOM}
              mapContainerStyle={containerStyle}
              onClick={handelClickOnMap}
              onUnmount={onUnmount}
              options={{
                streetViewControl: false,
                fullscreenControl: false,
                zoomControlOptions: { position: 999999 }
              }}
            >
              <Marker
                position={markerPosition}
                draggable={true}
                onDragEnd={handelClickOnMap}
              />
            </GoogleMap>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="tw-flex-shrink-0 tw-border-t tw-p-6">
        <div className="tw-flex tw-justify-between tw-gap-6">
          <ButtonMC
            text={t('customer.address.map.cancel')}
            onClick={handleClose}
            variant="GRAY"
            className="tw-w-full"
            size="medium"
          />
          <ButtonMC
            text={t('customer.address.map.save')}
            onClick={handleToggleModal}
            variant="PRIMARY"
            disabled={isResolvingAddress}
            className="tw-w-full"
            size="medium"
          />
        </div>
      </div>
    </div>
  );

  if (!renderModal) {
    return mapContent;
  }

  return (
    <RightModalMC
      title={t('customer.address.map.title')}
      isOpen={true}
      onClose={handleClose}
    >
      {mapContent}
    </RightModalMC>
  );
};

export default memo(MapComponentMC);
